import { type FC, useCallback, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { useAllOpenPaymentsQueryAll } from "../api/queries";
import { useFilteredInvoices } from "../hooks/useFilteredInvoices";
import {
  bucketLabelMap,
  intentionStatusCardOrder,
  intentionStatusData,
} from "../utils/consts";
import {
  displayCurrencyAmount,
  displayIntentionStatus,
} from "../utils/display";
import type {
  BucketOption,
  IntentionStatus,
  LineChartDatum,
} from "../utils/types";

type AnalyticsTooltipProps = {
  active?: boolean;
  label?: string | number;
  payload?: ReadonlyArray<{
    payload: LineChartDatum;
  }>;
};

const Analytics: FC = () => {
  const { data: invoices = [], isLoading } = useAllOpenPaymentsQueryAll();

  const [bucket, setBucket] = useState<BucketOption>("next_7_days");
  const [visibleStatuses, setVisibleStatuses] = useState<IntentionStatus[]>(
    intentionStatusCardOrder,
  );

  const toggleStatusVisibility = useCallback((status: IntentionStatus) => {
    setVisibleStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status],
    );
  }, []);

  const filteredInvoices = useFilteredInvoices(invoices, bucket);

  const chartData = useMemo(() => {
    if (!filteredInvoices.length) return [];

    const dateMap = new Map<string, LineChartDatum>();

    filteredInvoices.forEach((invoice) => {
      const dueDate = new Date(invoice.due_date);
      if (Number.isNaN(dueDate.getTime())) {
        return;
      }

      const dateKey = dueDate.toISOString().split("T")[0];

      let entry = dateMap.get(dateKey);

      if (!entry) {
        entry = {
          dateISO: dateKey,
          due_date_label: dueDate.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          }),
          Approved: 0,
          Unresolved: 0,
          No_reply: 0,
          In_progress: 0,
          Needs_attention: 0,
          invoicesByStatus: {
            Approved: [],
            Unresolved: [],
            No_reply: [],
            In_progress: [],
            Needs_attention: [],
          },
          dailyTotals: {
            Approved: 0,
            Unresolved: 0,
            No_reply: 0,
            In_progress: 0,
            Needs_attention: 0,
          },
        };
        dateMap.set(dateKey, entry);
      }

      entry[invoice.intention_status] += invoice.amount;
      entry.dailyTotals[invoice.intention_status] += invoice.amount;
      entry.invoicesByStatus[invoice.intention_status].push({
        externalId: invoice.external_id,
        amount: invoice.amount,
        currency: invoice.currency_type,
      });
    });

    // Convert Map to array and sort by dateISO to ensure chronological order
    const groupedByDate = Array.from(dateMap.values()).sort((a, b) =>
      a.dateISO.localeCompare(b.dateISO),
    );

    const runningTotals: Record<IntentionStatus, number> = {
      Approved: 0,
      Unresolved: 0,
      No_reply: 0,
      In_progress: 0,
      Needs_attention: 0,
    };

    return groupedByDate.map((entry) => {
      const stackedEntry: LineChartDatum = { ...entry };

      intentionStatusCardOrder.forEach((status) => {
        runningTotals[status] += entry[status];
        stackedEntry[status] = runningTotals[status];
      });

      return stackedEntry;
    });
  }, [filteredInvoices]);

  const renderTooltip = useCallback(
    ({ active, payload }: AnalyticsTooltipProps) => {
      if (!active || !payload?.length) return null;

      const datum = payload[0]?.payload as LineChartDatum | undefined;
      if (!datum) return null;

      const visible = intentionStatusCardOrder.filter((status) =>
        visibleStatuses.includes(status),
      );

      const hasInvoices = visible.some(
        (status) => datum.invoicesByStatus[status]?.length,
      );

      if (!hasInvoices) {
        return (
          <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-500 shadow-lg">
            No invoices for the selected statuses.
          </div>
        );
      }

      return (
        <div className="min-w-[220px] max-w-xs rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-lg">
          <p className="mb-2 font-semibold text-slate-700">
            {datum.due_date_label}
          </p>
          <div className="flex flex-col gap-3">
            {visible.map((status) => {
              const invoices = datum.invoicesByStatus[status];
              if (!invoices?.length) return null;

              return (
                <div key={status} className="space-y-1">
                  <p className="font-semibold uppercase tracking-wide text-slate-500">
                    {displayIntentionStatus(status)}
                  </p>
                  <ul className="space-y-1">
                    {invoices.map((invoice, index) => (
                      <li
                        key={`${invoice.externalId}-${index}`}
                        className="flex items-center justify-between gap-3 text-slate-600"
                      >
                        <span className="truncate">{invoice.externalId}</span>
                        <span className="font-medium text-slate-700">
                          {displayCurrencyAmount(
                            invoice.amount,
                            invoice.currency,
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      );
    },
    [visibleStatuses],
  );

  const currency =
    filteredInvoices[0]?.currency_type ?? invoices?.[0]?.currency_type ?? "USD";

  const latestTotals = useMemo(() => {
    if (!chartData.length) {
      return {
        Approved: 0,
        Unresolved: 0,
        No_reply: 0,
        In_progress: 0,
        Needs_attention: 0,
      } satisfies Record<IntentionStatus, number>;
    }

    const lastEntry = chartData[chartData.length - 1];

    return intentionStatusCardOrder.reduce(
      (acc, status) => ({
        ...acc,
        [status]: lastEntry[status],
      }),
      {
        Approved: 0,
        Unresolved: 0,
        No_reply: 0,
        In_progress: 0,
        Needs_attention: 0,
      } as Record<IntentionStatus, number>,
    );
  }, [chartData]);

  return (
    <section className="flex bg-green-300 rounded-lg h-full flex-col gap-6 px-6 py-8 lg:px-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Cash Flow Forecast
          </h1>
          <p className="text-sm text-slate-500">
            Monitor invoice performance with the latest payment pipeline
            insights.
          </p>
        </div>

        <select
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
          value={bucket}
          onChange={(event) => setBucket(event.target.value as BucketOption)}
        >
          {Object.entries(bucketLabelMap).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </header>

      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {intentionStatusCardOrder.map((status) => {
          const isActive = visibleStatuses.includes(status);

          return (
            <button
              key={status}
              type="button"
              onClick={() => toggleStatusVisibility(status)}
              className={`flex flex-col items-start rounded-2xl border p-4 text-left shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                isActive
                  ? "border-transparent bg-white focus-visible:ring-emerald-300"
                  : "border-dashed border-slate-200 bg-slate-50 text-slate-400 focus-visible:ring-slate-200"
              }`}
            >
              <div
                className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                  isActive
                    ? `bg-${intentionStatusData[status].tableIconColor}-100`
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {displayIntentionStatus(status)}
              </div>
              <div className="mt-3 text-2xl font-semibold">
                {displayCurrencyAmount(latestTotals[status] ?? 0, currency)}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Toggle to {isActive ? "hide" : "show"} this trend line
              </p>
            </button>
          );
        })}
      </section>

      <div className="flex-1 rounded-2xl border border-slate-100 bg-gray-50 p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-medium text-slate-800">
          Invoice Amounts by Due Date
        </h2>

        <div className="h-96 w-full min-h-0" style={{ minHeight: "384px" }}>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={384}>
              <LineChart
                data={chartData}
                margin={{ top: 16, right: 24, bottom: 0, left: 16 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="due_date_label" tick={{ fill: "#475569" }} />
                <YAxis
                  width={90}
                  tick={{ fill: "#475569" }}
                  tickFormatter={(value) =>
                    displayCurrencyAmount(Number(value), currency)
                  }
                />
                <Tooltip
                  content={renderTooltip}
                  cursor={{ stroke: "#CBD5F5", strokeWidth: 1 }}
                  wrapperStyle={{ outline: "none" }}
                />
                <Legend />
                {intentionStatusCardOrder.map((status) =>
                  visibleStatuses.includes(status) ? (
                    <Line
                      key={status}
                      type="monotone"
                      dataKey={status}
                      name={displayIntentionStatus(status)}
                      stroke={intentionStatusData[status].tableIconColor}
                      strokeWidth={3}
                      dot={{
                        stroke: intentionStatusData[status].tableIconColor,
                        strokeWidth: 2,
                        r: 4,
                      }}
                      activeDot={{ r: 6 }}
                    />
                  ) : null,
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-slate-500">
              No invoices match the selected timeline.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Analytics;
